import { AxiosError, type AxiosResponse } from "axios";

/** HTTP 请求错误中的请求上下文。 */
export type HttpClientErrorRequest = {
  method: string;
  url: string;
  traceId: string | null;
  sessionId: string | null;
};

/** 统一 HTTP 错误对象的构造参数。 */
export type HttpClientErrorOptions = {
  message: string;
  statusCode: number | null;
  code: string | null;
  retriable: boolean;
  request: HttpClientErrorRequest;
  cause: unknown;
  responseBody?: unknown;
};

/** 权限不足时给用户看的稳定中文提示。 */
export const FORBIDDEN_USER_MESSAGE =
  "当前账号没有执行该操作的权限，请联系管理员开通。";

/** 统一 HTTP 错误类型，屏蔽 axios 与服务端差异。 */
export class HttpClientError extends Error {
  readonly statusCode: number | null;

  readonly code: string | null;

  readonly retriable: boolean;

  readonly request: HttpClientErrorRequest;

  readonly responseBody: unknown;

  constructor(options: HttpClientErrorOptions) {
    super(options.message, { cause: options.cause });
    this.name = "HttpClientError";
    this.statusCode = options.statusCode;
    this.code = options.code;
    this.retriable = options.retriable;
    this.request = options.request;
    this.responseBody = options.responseBody;
  }
}

/** 判断对象是否为统一 HTTP 错误。 */
export function isHttpClientError(error: unknown): error is HttpClientError {
  return error instanceof HttpClientError;
}

/** 判断是否为 axios 默认失败文案。 */
export function isGenericHttpFailureMessage(message: string): boolean {
  return /^Request failed with status code \d+$/i.test(message.trim());
}

const CHINESE_TEXT_PATTERN = /[\u3400-\u9fff]/;

/** 判断错误文案是否适合直接展示给业务用户。 */
export function isReadableHttpErrorMessage(message: string | null | undefined): boolean {
  const text = message?.trim() ?? "";
  if (!text || isGenericHttpFailureMessage(text) || isOpaqueForbiddenDetail(text)) {
    return false;
  }
  return true;
}

/**
 * 优先展示后端可读业务文案；语义判重失败、422 与通用英文技术文案给出中文兜底。
 * 各功能域可再叠加自己的冲突码映射。
 */
export function resolveReadableHttpErrorMessage(
  error: unknown,
  fallback: string,
  options?: {
    conflictCode?: string;
    conflictFallback?: string;
    opaqueConflictDetails?: string[];
    validationFallback?: string;
  },
): string {
  if (!isHttpClientError(error)) {
    if (
      error instanceof Error
      && isReadableHttpErrorMessage(error.message)
      && CHINESE_TEXT_PATTERN.test(error.message)
    ) {
      return error.message.trim();
    }
    return fallback;
  }

  const detail = error.message?.trim() ?? "";
  const opaqueDetails = new Set(options?.opaqueConflictDetails ?? []);
  const hasReadableDetail =
    isReadableHttpErrorMessage(detail) && !opaqueDetails.has(detail);

  if (options?.conflictCode && error.code === options.conflictCode) {
    return hasReadableDetail ? detail : (options.conflictFallback ?? fallback);
  }
  if (error.code === "SEMANTIC_DUPLICATE_CHECK_FAILED") {
    return hasReadableDetail
      ? detail
      : "名称语义判重失败，请稍后重试。";
  }
  if (error.statusCode === 422) {
    return hasReadableDetail
      ? detail
      : (options?.validationFallback ?? "请完整填写必填项后再保存。");
  }
  if (hasReadableDetail) {
    return detail;
  }
  return fallback;
}

/** 后端权限拒绝常用的不可读 detail。 */
function isOpaqueForbiddenDetail(message: string): boolean {
  const normalized = message.trim().toLowerCase();
  return (
    normalized === "forbidden" ||
    normalized === "forbidden." ||
    normalized === "access denied" ||
    normalized === "access denied."
  );
}

/** 把 403 错误整理成可展示中文。 */
export function resolveForbiddenUserMessage(message: string | null | undefined): string {
  const text = message?.trim() ?? "";
  if (!text || isGenericHttpFailureMessage(text) || isOpaqueForbiddenDetail(text)) {
    return FORBIDDEN_USER_MESSAGE;
  }
  return text;
}

/** 从服务端响应中提取可展示的错误信息。 */
function pickMessageFromResponse(response: AxiosResponse | undefined): string | null {
  if (!response) {
    return null;
  }

  const payload = response.data;
  if (typeof payload === "string" && payload.trim().length > 0) {
    return payload;
  }

  if (payload && typeof payload === "object") {
    const dataMessage = Reflect.get(payload, "message");
    if (typeof dataMessage === "string" && dataMessage.trim().length > 0) {
      return dataMessage;
    }

    const dataDetail = Reflect.get(payload, "detail");
    if (typeof dataDetail === "string" && dataDetail.trim().length > 0) {
      return dataDetail;
    }

    const validationMessage = pickValidationDetailMessage(dataDetail);
    if (validationMessage) {
      return validationMessage;
    }

    // FastAPI HTTPException 常把业务错误放在 detail 对象中；优先展示
    // 其中的 message，避免页面只看到无意义的 "Request failed with status code"。
    if (dataDetail && typeof dataDetail === "object" && !Array.isArray(dataDetail)) {
      const detailMessage = Reflect.get(dataDetail, "message");
      if (typeof detailMessage === "string" && detailMessage.trim().length > 0) {
        return detailMessage;
      }
    }
  }

  return null;
}

/** 从 FastAPI/Pydantic 422 校验数组中提取可展示中文文案。 */
function pickValidationDetailMessage(detail: unknown): string | null {
  if (!Array.isArray(detail) || detail.length === 0) {
    return null;
  }
  const messages: string[] = [];
  for (const item of detail) {
    if (!item || typeof item !== "object") {
      continue;
    }
    const rawMsg = Reflect.get(item, "msg");
    if (typeof rawMsg !== "string") {
      continue;
    }
    const cleaned = stripPydanticValueErrorPrefix(rawMsg);
    if (cleaned && !messages.includes(cleaned)) {
      messages.push(cleaned);
    }
  }
  return messages.length > 0 ? messages.join("；") : null;
}

/** 去掉 Pydantic v2 常见的 ``Value error, `` 前缀。 */
function stripPydanticValueErrorPrefix(message: string): string {
  return message.replace(/^Value error,\s*/i, "").trim();
}

/** 从统一失败响应中提取后端业务错误码。 */
function pickCodeFromResponse(response: AxiosResponse | undefined): string | null {
  if (!response) {
    return null;
  }

  const payload = response.data;
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const responseCode = Reflect.get(payload, "code");
  if (typeof responseCode === "string" && responseCode.trim().length > 0) {
    return responseCode;
  }

  const detail = Reflect.get(payload, "detail");
  if (detail && typeof detail === "object") {
    const detailCode = Reflect.get(detail, "code");
    if (typeof detailCode === "string" && detailCode.trim().length > 0) {
      return detailCode;
    }
  }

  return null;
}

/** 规范化请求方法字段。 */
function normalizeMethod(method: string | undefined): string {
  return (method ?? "unknown").toLowerCase();
}

/** 判断状态码是否具备重试价值。 */
function isRetriableStatus(statusCode: number | null): boolean {
  if (statusCode === null) {
    return true;
  }
  return statusCode === 408 || statusCode === 429 || statusCode >= 500;
}

/** 将任意异常归一化为 HttpClientError。 */
export function normalizeHttpError(error: unknown): HttpClientError {
  if (isHttpClientError(error)) {
    return error;
  }

  if (error instanceof AxiosError) {
    const statusCode = error.response?.status ?? null;
    const url = error.config?.url ?? "unknown";
    const method = normalizeMethod(error.config?.method);
    const traceId = error.config?.headers?.["x-trace-id"] ?? null;
    const sessionId = error.config?.headers?.["x-session-id"] ?? null;
    const rawMessage =
      pickMessageFromResponse(error.response) ?? error.message ?? "HTTP request failed";
    const message =
      statusCode === 403 ? resolveForbiddenUserMessage(rawMessage) : rawMessage;
    const responseCode = pickCodeFromResponse(error.response);

    return new HttpClientError({
      cause: error,
      code: responseCode ?? error.code ?? null,
      responseBody: error.response?.data,
      message,
      request: {
        method,
        sessionId,
        traceId,
        url,
      },
      retriable: isRetriableStatus(statusCode),
      statusCode,
    });
  }

  if (error instanceof Error) {
    return new HttpClientError({
      cause: error,
      code: null,
      message: error.message,
      request: {
        method: "unknown",
        sessionId: null,
        traceId: null,
        url: "unknown",
      },
      retriable: false,
      statusCode: null,
    });
  }

  return new HttpClientError({
    cause: error,
    code: null,
    message: "Unknown request error",
    request: {
      method: "unknown",
      sessionId: null,
      traceId: null,
      url: "unknown",
    },
    retriable: false,
    statusCode: null,
  });
}
